import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/util/cn';

/**
 * Inline status pill — used for assignment states, rollout phase tags,
 * mandate compliance markers, and the audit-event category column.
 * Semantic tokens drive the colour; the family stays consistent across
 * themes.
 */

const badgeVariants = cva(
  [
    'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5',
    'text-2xs font-medium leading-none whitespace-nowrap capitalize',
  ].join(' '),
  {
    variants: {
      tone: {
        neutral: 'border-border bg-surface-inset text-fg-muted',
        info: 'border-info/40 bg-info-soft text-info',
        // STATUS green — deliberately NOT the direction token (--long is the
        // blue "up" hue; a success badge must stay green across themes).
        success: 'border-success/40 bg-success-soft text-success',
        warn: 'border-warn/40 bg-warn-soft text-warn',
        crit: 'border-crit/40 bg-crit-soft text-crit',
        accent: 'border-accent/40 bg-accent-soft text-accent',
        // DIRECTION, not status. buy/sell and long/short belong here — using
        // success/crit for a side is the exact mix-up the note above warns
        // about, and it was happening because these tones simply did not exist.
        long: 'border-long/40 bg-long-soft text-long',
        short: 'border-short/40 bg-short-soft text-short',
        rollout1: 'border-rollout-p1/40 bg-surface-inset text-rollout-p1',
        rollout2: 'border-rollout-p2/40 bg-surface-inset text-rollout-p2',
        rollout3: 'border-rollout-p3/40 bg-surface-inset text-rollout-p3',
        rollout4: 'border-rollout-p4/40 bg-surface-inset text-rollout-p4',
        mandateOk: 'border-mandate-ok/40 bg-surface-inset text-mandate-ok',
        mandateWarn: 'border-mandate-warn/40 bg-surface-inset text-mandate-warn',
        mandateBreach: 'border-mandate-breach/40 bg-surface-inset text-mandate-breach',
      },
      size: {
        sm: 'h-5 text-2xs',
        md: 'h-6 text-xs',
        lg: 'h-7 px-3 text-xs',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'md' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, tone, size, ...props }, ref) => (
    <span ref={ref} className={cn(badgeVariants({ tone, size }), className)} {...props} />
  ),
);
Badge.displayName = 'Badge';

export { badgeVariants };
